import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Coins, FileSpreadsheet, FileText, LogOut, MapPin, Menu, ShieldCheck, Users, X } from "lucide-react";

import { useAuth } from "../AuthContext";
import style from "../style/components/HeaderV2.module.css";

const navigation = [
    { text: "키오스크 관리", router: "/", icon: MapPin },
    { text: "회원/적립 현황", router: "/members", icon: Users },
    { text: "계정 관리", router: "/admin", icon: ShieldCheck },
    { text: "엑셀 출력", router: "/excel", icon: FileSpreadsheet },
    { text: "포인트 관리", router: "/point", icon: Coins },
    { text: "공지사항 관리", router: "/notice", icon: FileText },
];

const HeaderV2 = () => {
    const [openMenu, setOpenMenu] = useState<boolean>(false);
    const location = useLocation();
    const { userType, partnerId, signOutUser } = useAuth();

    useEffect(() => {
        document.body.style.overflow = openMenu ? "hidden" : "auto";
    }, [openMenu]);

    const logout = async () => {
        try {
            await signOutUser();
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <>
            <aside className={style["sidebar"]}>
                <SidebarContent activePath={location.pathname} userType={userType} partnerId={partnerId} onLogout={logout} />
            </aside>

            {openMenu && (
                <div className={style["drawer"]} onClick={() => setOpenMenu(false)}>
                    <div className={style["drawer-panel"]} onClick={(event) => event.stopPropagation()}>
                        <div className={style["drawer-close"]}>
                            <button className={style["icon-button"]} onClick={() => setOpenMenu(false)} aria-label="메뉴 닫기">
                                <X size={18} />
                            </button>
                        </div>
                        <SidebarContent
                            activePath={location.pathname}
                            userType={userType}
                            partnerId={partnerId}
                            onNavigate={() => setOpenMenu(false)}
                            onLogout={logout}
                        />
                    </div>
                </div>
            )}

            <header className={style["topbar"]}>
                <div className={style["topbar-left"]}>
                    <button className={[style["icon-button"], style["mobile-menu"]].join(" ")} onClick={() => setOpenMenu(true)}>
                        <Menu size={18} />
                    </button>
                    <span className={style["topbar-date"]}>{formatToday()}</span>
                    <span className={style["topbar-divider"]} />
                    <span className={style["topbar-role"]}>{formatRole(userType)}</span>
                </div>

                <div className={style["topbar-actions"]}>
                    <QuickAction to="/" text="키오스크" />
                    <QuickAction to="/point" text="포인트" />
                    <QuickAction to="/excel" text="엑셀" />
                </div>
            </header>
        </>
    );
};

const SidebarContent = ({
    activePath,
    userType,
    partnerId,
    onNavigate,
    onLogout,
}: {
    activePath: string;
    userType: string | null;
    partnerId: string | null;
    onNavigate?: () => void;
    onLogout: () => void;
}) => {
    return (
        <>
            <div className={style["brand"]}>
                <div className={style["brand-mark"]}>I</div>
                <div>
                    <p className={style["brand-title"]}>Partner</p>
                    <p className={style["brand-subtitle"]}>{partnerId || "Workspace"}</p>
                </div>
            </div>
            <nav className={style["nav"]}>
                {navigation.map((item) => {
                    const active = item.router === "/" ? activePath === "/" : activePath.startsWith(item.router);
                    const Icon = item.icon;

                    return (
                        <Link
                            key={item.router}
                            to={item.router}
                            className={[style["nav-link"], active && style["active"]].filter(Boolean).join(" ")}
                            onClick={onNavigate}
                        >
                            <Icon size={18} />
                            {item.text}
                        </Link>
                    );
                })}
            </nav>
            <div className={style["sidebar-profile"]}>
                <div className={style["profile-row"]}>
                    <div className={style["avatar"]}>파</div>
                    <div className={style["profile-text"]}>
                        <div className={style["profile-name"]}>파트너 관리자</div>
                        <div className={style["profile-role"]}>{formatRole(userType)}</div>
                    </div>
                </div>
                <button className={style["logout-button"]} onClick={onLogout}>
                    <LogOut size={15} />
                    로그아웃
                </button>
            </div>
        </>
    );
};

const QuickAction = ({ to, text }: { to: string; text: string }) => (
    <Link className={style["quick-action"]} to={to}>
        {text}
    </Link>
);

const formatRole = (userType: string | null) => {
    if (userType === "GROUP_ADMIN") return "파트너 관리자";
    if (userType === "ADMIN") return "관리자";
    return "파트너";
};

const formatToday = () => {
    const date = new Date();
    return date.toLocaleDateString("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        weekday: "short",
    });
};

export default HeaderV2;
